import React, { useState, useEffect } from 'react';
import { useNavigate } from 'react-router-dom';
import {
  Plus,
  Edit,
  AlertCircle,
  Calendar,
  DollarSign,
  FileText,
  Users,
  ArrowRight,
  CheckCircle,
  Image,
  Clock,
  Globe,
  TestTube,
  Copy,
  ExternalLink
} from 'lucide-react';
import FeeWaiverList from './FeeWaiverList';
import '../../../styles/SubmissionTab.css';

/**
 * SubmissionTab Component
 * 
 * Displays call out management for a collection.
 * 
 * Rules:
 * - If no event tied to collection → show "Create Event" option
 * - If event exists but no call out → show "Create Call Out" option
 * - If call out exists → show summary with status and "Edit" option
 * 
 * Props:
 * - collection: object (contains gallery_id, collection_id, title)
 * - eventDetails: object or null (linked event data)
 * - submissionDetails: object or null (existing submission data)
 * - loading: boolean
 * - onCreateSubmission: function
 * - onEditSubmission: function
 * - onCreateEvent: function
 * - onAddArtwork: function (optional - for adding artwork directly)
 */
const SubmissionTab = ({
  collection,
  eventDetails,
  submissionDetails,
  loading,
  onCreateSubmission,
  onEditSubmission,
  onCreateEvent,
  onAddArtwork
}) => {
  const navigate = useNavigate();
  const [copied, setCopied] = useState(false);

  // Get status badge color and text
  const getStatusBadge = (status) => {
    switch (status) {
      case 'live':
        return { color: '#16a34a', bg: '#dcfce7', text: 'Live' };
      case 'testing':
        return { color: '#ca8a04', bg: '#fef9c3', text: 'Testing' };
      case 'closed':
        return { color: '#dc2626', bg: '#fee2e2', text: 'Closed' };
      case 'draft':
      default:
        return { color: '#6b7280', bg: '#f3f4f6', text: 'Draft' };
    }
  };

  const copyToClipboard = (text) => {
    navigator.clipboard.writeText(text);
    setCopied(true);
    setTimeout(() => setCopied(false), 2000);
  };

  const getPublicUrl = () => {
    if (!submissionDetails?.submission_id) return null;
    return `https://share.vibrantcast.com/submission?submissionId=${submissionDetails.submission_id}`;
  };

  const getTestUrl = () => {
    if (!submissionDetails?.submission_id || !submissionDetails?.test_token) return null;
    return `https://share.vibrantcast.com/submission?submissionId=${submissionDetails.submission_id}&test=${submissionDetails.test_token}`;
  };

  if (loading) {
    return (
      <div className="submission-tab loading-state">
        <div className="loading-spinner"></div>
        <p>Loading call out details...</p>
      </div>
    );
  }

  // STATE 1: No Event Associated
  if (!eventDetails) {
    return (
      <div className="submission-tab no-event-state">
        <div className="empty-state-card">
          <div className="empty-state-icon alert">
            <AlertCircle size={48} />
          </div>
          <h3>No Exhibition Event</h3>
          <p>You must create or link an exhibition event to this collection before you can create a call out.</p>
          
          <div className="action-buttons">
            <button
              onClick={onCreateEvent}
              className="btn-primary action-btn"
            >
              <Plus size={18} />
              Create Exhibition Event
            </button>
            <button
              onClick={() => navigate(-1)}
              className="btn-secondary action-btn"
            >
              Go Back
            </button>
          </div>

          <div className="info-card">
            <h4>📋 How it works:</h4>
            <ol>
              <li>Create an exhibition event for this collection</li>
              <li>Set event dates, venue details, and ticket info</li>
              <li>Then create a call out to invite artists</li>
              <li>Artists can apply and submit their work</li>
            </ol>
          </div>
        </div>
      </div>
    );
  }

  // STATE 2: Event Exists but No Submission Yet
  if (!submissionDetails) {
    return (
      <div className="submission-tab no-submission-state">
        {/* Add Artwork First - Primary Action */}
        {onAddArtwork && (
          <div className="add-artwork-primary">
            <div className="add-artwork-content">
              <div className="add-artwork-icon">
                <Image size={32} />
              </div>
              <div className="add-artwork-text">
                <h3>Add Artwork to Collection</h3>
                <p>Browse and add existing artworks from any artist to your collection</p>
              </div>
              <button
                onClick={onAddArtwork}
                className="btn-primary add-artwork-btn-large"
              >
                <Image size={20} />
                Browse & Add Artwork
              </button>
            </div>
          </div>
        )}

        {/* Call Out to Artists - Secondary */}
        <div className="create-submission-section">
          <h3>📢 Call Out to Artists</h3>
          <p>Create a call out to invite artists to participate in "{collection.title}"</p>
          
          <button
            onClick={onCreateSubmission}
            className="btn-primary create-btn"
          >
            <Plus size={20} />
            Create Call Out
          </button>

          <div className="submission-info">
            <p className="info-text">
              You'll be able to:
            </p>
            <ul className="feature-list">
              <li>Set deadline and fee</li>
              <li>Add guidelines and theme</li>
              <li>Configure artist application form</li>
              <li>Send artist notifications</li>
              <li>Manage acceptance and check-in</li>
            </ul>
          </div>
        </div>
      </div>
    );
  }

  // STATE 3: Submission Exists - Show Summary
  const status = getStatusBadge(submissionDetails.status);
  const publicUrl = getPublicUrl();
  const testUrl = getTestUrl();
  const isDeadlinePassed = submissionDetails.submission_deadline && new Date(submissionDetails.submission_deadline) < new Date();

  return (
    <div className="submission-tab submission-exists-state">
      {/* Header with Status */}
      <div style={{
        display: 'flex',
        justifyContent: 'space-between',
        alignItems: 'flex-start',
        marginBottom: '24px',
        flexWrap: 'wrap',
        gap: '16px'
      }}>
        <div>
          <div style={{ display: 'flex', alignItems: 'center', gap: '12px', marginBottom: '8px' }}>
            <h2 style={{ margin: 0 }}>{submissionDetails.title}</h2>
            <span style={{
              padding: '4px 12px',
              borderRadius: '9999px',
              fontSize: '13px',
              fontWeight: '600',
              backgroundColor: status.bg,
              color: status.color
            }}>
              {status.text}
            </span>
          </div>
          {submissionDetails.callout_headline && (
            <p style={{ margin: 0, color: '#6b7280', fontSize: '14px' }}>
              {submissionDetails.callout_headline}
            </p>
          )}
        </div>
        <button
          onClick={onEditSubmission}
          style={{
            display: 'inline-flex',
            alignItems: 'center',
            gap: '8px',
            padding: '10px 20px',
            backgroundColor: '#8b5cf6',
            color: 'white',
            border: 'none',
            borderRadius: '8px',
            fontSize: '14px',
            fontWeight: '600',
            cursor: 'pointer'
          }}
        >
          <Edit size={16} />
          Edit Call Out
        </button>
      </div>

      {/* Key Info Cards */}
      <div style={{
        display: 'grid',
        gridTemplateColumns: 'repeat(auto-fit, minmax(200px, 1fr))',
        gap: '16px',
        marginBottom: '24px'
      }}>
        {/* Deadline */}
        <div style={{
          padding: '16px',
          backgroundColor: isDeadlinePassed ? '#fef2f2' : '#f8fafc',
          borderRadius: '12px',
          border: `1px solid ${isDeadlinePassed ? '#fecaca' : '#e2e8f0'}`
        }}>
          <div style={{ display: 'flex', alignItems: 'center', gap: '8px', marginBottom: '8px' }}>
            <Calendar size={18} color={isDeadlinePassed ? '#dc2626' : '#64748b'} />
            <span style={{ fontSize: '13px', color: '#64748b', fontWeight: '500' }}>Deadline</span>
          </div>
          <p style={{ margin: 0, fontSize: '16px', fontWeight: '600', color: isDeadlinePassed ? '#dc2626' : '#1e293b' }}>
            {submissionDetails.submission_deadline 
              ? new Date(submissionDetails.submission_deadline).toLocaleDateString('en-US', { 
                  month: 'short', day: 'numeric', year: 'numeric' 
                })
              : 'Not set'}
          </p>
          {isDeadlinePassed && (
            <span style={{ fontSize: '12px', color: '#dc2626' }}>Deadline passed</span>
          )}
        </div>

        {/* Price */}
        <div style={{
          padding: '16px',
          backgroundColor: '#f8fafc',
          borderRadius: '12px',
          border: '1px solid #e2e8f0'
        }}>
          <div style={{ display: 'flex', alignItems: 'center', gap: '8px', marginBottom: '8px' }}>
            <DollarSign size={18} color="#64748b" />
            <span style={{ fontSize: '13px', color: '#64748b', fontWeight: '500' }}>Fee</span>
          </div>
          <p style={{ margin: 0, fontSize: '16px', fontWeight: '600', color: '#1e293b' }}>
            {submissionDetails.submission_price === 0 || !submissionDetails.submission_price
              ? 'Free' 
              : `$${parseFloat(submissionDetails.submission_price).toFixed(2)}`}
          </p>
          {submissionDetails.member_free && submissionDetails.submission_price > 0 && (
            <span style={{ fontSize: '12px', color: '#16a34a' }}>Free for members</span>
          )}
        </div>

        {/* Max Per Artist */}
        <div style={{
          padding: '16px',
          backgroundColor: '#f8fafc',
          borderRadius: '12px',
          border: '1px solid #e2e8f0'
        }}>
          <div style={{ display: 'flex', alignItems: 'center', gap: '8px', marginBottom: '8px' }}>
            <FileText size={18} color="#64748b" />
            <span style={{ fontSize: '13px', color: '#64748b', fontWeight: '500' }}>Max Per Artist</span>
          </div>
          <p style={{ margin: 0, fontSize: '16px', fontWeight: '600', color: '#1e293b' }}>
            {submissionDetails.max_allowed || 1} artwork{(submissionDetails.max_allowed || 1) !== 1 ? 's' : ''}
          </p>
        </div>

        {/* Open Date (if set) */}
        {submissionDetails.open_date && (
          <div style={{
            padding: '16px',
            backgroundColor: '#f8fafc',
            borderRadius: '12px',
            border: '1px solid #e2e8f0'
          }}>
            <div style={{ display: 'flex', alignItems: 'center', gap: '8px', marginBottom: '8px' }}>
              <Clock size={18} color="#64748b" />
              <span style={{ fontSize: '13px', color: '#64748b', fontWeight: '500' }}>Opens</span>
            </div>
            <p style={{ margin: 0, fontSize: '16px', fontWeight: '600', color: '#1e293b' }}>
              {new Date(submissionDetails.open_date).toLocaleDateString('en-US', { 
                month: 'short', day: 'numeric', year: 'numeric' 
              })}
            </p>
          </div>
        )}
      </div>

      {/* URL Section based on status */}
      {submissionDetails.status === 'live' && publicUrl && (
        <div style={{
          padding: '16px',
          backgroundColor: '#f0fdf4',
          borderRadius: '12px',
          border: '1px solid #bbf7d0',
          marginBottom: '24px'
        }}>
          <div style={{ display: 'flex', alignItems: 'center', gap: '8px', marginBottom: '12px' }}>
            <Globe size={18} color="#16a34a" />
            <span style={{ fontSize: '14px', fontWeight: '600', color: '#16a34a' }}>Public Call Out Page</span>
          </div>
          <div style={{ display: 'flex', gap: '8px', flexWrap: 'wrap' }}>
            <a
              href={publicUrl}
              target="_blank"
              rel="noopener noreferrer"
              style={{
                display: 'inline-flex',
                alignItems: 'center',
                gap: '6px',
                padding: '8px 16px',
                backgroundColor: '#16a34a',
                color: 'white',
                borderRadius: '6px',
                fontSize: '14px',
                fontWeight: '500',
                textDecoration: 'none'
              }}
            >
              <ExternalLink size={14} />
              View Page
            </a>
            <button
              onClick={() => copyToClipboard(publicUrl)}
              style={{
                display: 'inline-flex',
                alignItems: 'center',
                gap: '6px',
                padding: '8px 16px',
                backgroundColor: 'white',
                color: '#374151',
                border: '1px solid #d1d5db',
                borderRadius: '6px',
                fontSize: '14px',
                fontWeight: '500',
                cursor: 'pointer'
              }}
            >
              <Copy size={14} />
              {copied ? 'Copied!' : 'Copy URL'}
            </button>
          </div>
        </div>
      )}

      {submissionDetails.status === 'testing' && testUrl && (
        <div style={{
          padding: '16px',
          backgroundColor: '#fefce8',
          borderRadius: '12px',
          border: '1px solid #fde047',
          marginBottom: '24px'
        }}>
          <div style={{ display: 'flex', alignItems: 'center', gap: '8px', marginBottom: '12px' }}>
            <TestTube size={18} color="#ca8a04" />
            <span style={{ fontSize: '14px', fontWeight: '600', color: '#ca8a04' }}>Testing Mode</span>
          </div>
          <p style={{ margin: '0 0 12px 0', fontSize: '13px', color: '#713f12' }}>
            Only testers with this link can access the call out page.
          </p>
          <div style={{ display: 'flex', gap: '8px', flexWrap: 'wrap' }}>
            <a
              href={testUrl}
              target="_blank"
              rel="noopener noreferrer"
              style={{
                display: 'inline-flex',
                alignItems: 'center',
                gap: '6px',
                padding: '8px 16px',
                backgroundColor: '#ca8a04',
                color: 'white',
                borderRadius: '6px',
                fontSize: '14px',
                fontWeight: '500',
                textDecoration: 'none'
              }}
            >
              <ExternalLink size={14} />
              Open Test Page
            </a>
            <button
              onClick={() => copyToClipboard(testUrl)}
              style={{
                display: 'inline-flex',
                alignItems: 'center',
                gap: '6px',
                padding: '8px 16px',
                backgroundColor: 'white',
                color: '#374151',
                border: '1px solid #d1d5db',
                borderRadius: '6px',
                fontSize: '14px',
                fontWeight: '500',
                cursor: 'pointer'
              }}
            >
              <Copy size={14} />
              {copied ? 'Copied!' : 'Copy Test URL'}
            </button>
          </div>
        </div>
      )}

      {submissionDetails.status === 'draft' && (
        <div style={{
          padding: '16px',
          backgroundColor: '#f3f4f6',
          borderRadius: '12px',
          border: '1px solid #e5e7eb',
          marginBottom: '24px'
        }}>
          <div style={{ display: 'flex', alignItems: 'center', gap: '8px', marginBottom: publicUrl ? '12px' : '0' }}>
            <AlertCircle size={18} color="#6b7280" />
            <span style={{ fontSize: '14px', color: '#6b7280' }}>
              This call out is in <strong>Draft</strong> mode and is not visible to artists. 
              Set status to <strong>Testing</strong> or <strong>Live</strong> to publish.
            </span>
          </div>
          {publicUrl && (
            <div style={{ display: 'flex', gap: '8px', flexWrap: 'wrap' }}>
              <a
                href={publicUrl}
                target="_blank"
                rel="noopener noreferrer"
                style={{
                  display: 'inline-flex',
                  alignItems: 'center',
                  gap: '6px',
                  padding: '8px 16px',
                  backgroundColor: '#6b7280',
                  color: 'white',
                  borderRadius: '6px',
                  fontSize: '14px',
                  fontWeight: '500',
                  textDecoration: 'none'
                }}
              >
                <ExternalLink size={14} />
                Preview Page
              </a>
              <button
                onClick={() => copyToClipboard(publicUrl)}
                style={{
                  display: 'inline-flex',
                  alignItems: 'center',
                  gap: '6px',
                  padding: '8px 16px',
                  backgroundColor: 'white',
                  color: '#374151',
                  border: '1px solid #d1d5db',
                  borderRadius: '6px',
                  fontSize: '14px',
                  fontWeight: '500',
                  cursor: 'pointer'
                }}
              >
                <Copy size={14} />
                {copied ? 'Copied!' : 'Copy URL'}
              </button>
            </div>
          )}
        </div>
      )}

      {/* Linked Event Info */}
      {eventDetails && (
        <div style={{
          padding: '16px',
          backgroundColor: '#f8fafc',
          borderRadius: '12px',
          border: '1px solid #e2e8f0'
        }}>
          <h4 style={{ margin: '0 0 12px 0', fontSize: '14px', color: '#64748b' }}>🎨 Linked Exhibition</h4>
          <p style={{ margin: 0, fontWeight: '600' }}>{eventDetails.name}</p>
          {eventDetails.event_start && (
            <p style={{ margin: '4px 0 0 0', fontSize: '13px', color: '#64748b' }}>
              {new Date(eventDetails.event_start).toLocaleDateString('en-US', { 
                weekday: 'long', month: 'long', day: 'numeric', year: 'numeric' 
              })}
            </p>
          )}
        </div>
      )}
      {/* Fee Waivers Section */}
      <FeeWaiverList 
        submissionId={submissionDetails.submission_id} 
        submissionTitle={submissionDetails.title}
      />
    </div>
  );
};

export default SubmissionTab;
